import { PageHead } from '../../components/ui/PageHead';
import { TableOfContents } from '../../components/navigation/TableOfContents';
import { ArticleBody } from '../../components/content/ArticleBody';
import styles from './SecurityPage.module.css';

const tocItems = [
  { id: 'overview', label: 'Security Overview' },
  { id: 'infrastructure', label: 'Infrastructure Security' },
  { id: 'data-security', label: 'Data Security' },
  { id: 'access-controls', label: 'Access Controls' },
  { id: 'compliance', label: 'Compliance Posture' },
  { id: 'incident-response', label: 'Incident Response' },
  { id: 'vendor-security', label: 'Vendor Security' },
  { id: 'disclosure', label: 'Responsible Disclosure' },
];

export function SecurityPage() {
  return (
    <>
      <PageHead
        title="Security"
        description="NAATS security posture - infrastructure security, data protection, access controls, and compliance certifications."
      />
      <div className={styles.inner}>
        <aside className={styles.sidebar}>
          <TableOfContents items={tocItems} />
        </aside>

        <div className={styles.content}>
          <ArticleBody>
            <h1>Security</h1>
            <p><em>Last updated: January 2026</em></p>

            <h2 id="overview">Security Overview</h2>
            <p>
              Security is foundational to everything we build at NAATS. As a provider of
              Medallion Lakehouse and Agentic AI platforms for regulated industries - including
              Financial Services, Healthcare, and Pharmaceuticals - we apply enterprise-grade
              security controls across every layer of our architecture, tooling, and delivery
              methodology.
            </p>

            <h2 id="infrastructure">Infrastructure Security</h2>
            <ul>
              <li><strong>Cloud Security:</strong> All deployments leverage cloud provider security controls (AWS, Azure, GCP) with hardened baseline configurations validated by our security team.</li>
              <li><strong>Network Isolation:</strong> Private VPCs, network segmentation, and zero-trust networking architectures are applied by default.</li>
              <li><strong>IaC Security Scanning:</strong> Terraform and Kubernetes manifests are scanned for security misconfigurations in CI/CD pipelines before any deployment.</li>
              <li><strong>Patch Management:</strong> Automated patching cadence for all managed infrastructure components with SLA-bound remediation for critical CVEs.</li>
            </ul>

            <h2 id="data-security">Data Security</h2>
            <ul>
              <li><strong>Encryption in Transit:</strong> All data transmitted between services uses TLS 1.2 or higher.</li>
              <li><strong>Encryption at Rest:</strong> All data stored in Lakehouse storage layers is encrypted using AES-256 or cloud-native KMS-managed keys.</li>
              <li><strong>Data Masking &amp; Tokenization:</strong> PII and sensitive data fields are masked or tokenized at Bronze ingestion using automated detection patterns.</li>
              <li><strong>Column-level Security:</strong> Fine-grained access controls at the column and row level prevent unauthorized data exposure within the Lakehouse.</li>
            </ul>

            <h2 id="access-controls">Access Controls</h2>
            <ul>
              <li><strong>Principle of Least Privilege:</strong> All IAM roles and service accounts are scoped to the minimum permissions required for function.</li>
              <li><strong>Multi-Factor Authentication:</strong> MFA is required for all administrative access to production environments.</li>
              <li><strong>Service Account Management:</strong> Workload identity and short-lived credential patterns replace long-lived static credentials.</li>
              <li><strong>Privileged Access Management:</strong> Just-in-time access provisioning for privileged operations with full audit logging.</li>
            </ul>

            <h2 id="compliance">Compliance Posture</h2>
            <p>NAATS platform architectures are designed to support the following compliance frameworks:</p>
            <ul>
              <li><strong>ISO 27001:</strong> Information security management system alignment.</li>
              <li><strong>SOC 2 Type II:</strong> Security, availability, and confidentiality controls assessment.</li>
              <li><strong>HIPAA:</strong> Healthcare data protection and PHI handling controls.</li>
              <li><strong>SOX:</strong> Financial data integrity and access control requirements.</li>
              <li><strong>FDA 21 CFR Part 11 / GxP:</strong> Pharmaceutical data validation and audit trail requirements.</li>
              <li><strong>GDPR:</strong> EU data subject rights and cross-border transfer controls.</li>
            </ul>

            <h2 id="incident-response">Incident Response</h2>
            <p>
              NAATS maintains a documented Incident Response Plan with defined severity levels,
              escalation paths, and communication protocols. In the event of a security incident
              affecting client environments, we commit to initial notification within 24 hours of
              confirmed discovery, with regular updates through resolution.
            </p>

            <h2 id="vendor-security">Vendor Security</h2>
            <p>
              All third-party vendors with access to sensitive data undergo security assessment
              prior to engagement, including review of their security posture, data processing
              agreements, and sub-processor disclosures.
            </p>

            <h2 id="disclosure">Responsible Disclosure</h2>
            <p>
              If you believe you have discovered a security vulnerability in NAATS systems,
              please report it responsibly via our{' '}
              <a href="/contact">contact page</a> with "Security Vulnerability" in the subject.
              We commit to acknowledging reports within 48 hours and working with reporters
              in good faith toward remediation.
            </p>
          </ArticleBody>
        </div>
      </div>
    </>
  );
}
